import { Controller } from "@hotwired/stimulus"

// Drives the "Create new resume" modal: blank/AI/PDF submit + launches the
// (reused) LinkedIn Reclaim flow. Posts to the resume create/import endpoints
// and redirects to the returned editUrl.
export default class extends Controller {
  static targets = ["name", "level", "targetToggle", "targetKnob", "targetFields",
                    "jtTitle", "jtCompany", "jtDesc", "error", "file", "aiBtn", "aiLabel", "pdfBtn", "pdfLabel",
                    "applyHeading", "applyName", "applySection", "exampleBtn"]
  static values = {
    createUrl: String, aiUrl: String, pdfUrl: String, linkedinUrl: String, paid: Boolean
  }

  connect() {
    // The controller element IS the modal. Portal the whole element (its targets +
    // actions move with it) to <body> so the dashboard's z-10 stacking context can't
    // trap the overlay. Moving within the same document keeps the controller connected.
    if (this.element.parentElement !== document.body) document.body.appendChild(this.element)
    // The open trigger lives outside this element, so wire it by marker attribute.
    // Pass the event so a gallery card's data-* (chosen template / example) is read.
    this._open = (e) => this.open(e)
    document.querySelectorAll("[data-create-resume-open]").forEach((b) => b.addEventListener("click", this._open))
  }

  disconnect() {
    document.querySelectorAll("[data-create-resume-open]").forEach((b) => b.removeEventListener("click", this._open))
  }

  open(e) {
    // Read the chosen template / example off the clicked trigger (gallery card).
    // Plain "Create new resume" triggers carry no dataset → blank create as before.
    const d = (e && e.currentTarget && e.currentTarget.dataset) || {}
    this._templateId   = d.templateId || ""
    this._exampleSlug  = d.exampleSlug || ""
    this._exampleUseUrl = d.exampleUseUrl || ""
    const inChooser = !!(this._templateId || this._exampleSlug)
    if (this.hasApplyHeadingTarget) this.applyHeadingTarget.classList.toggle("hidden", !inChooser)
    if (this.hasApplyNameTarget)    this.applyNameTarget.textContent = d.exampleTitle || d.templateName || "this design"
    if (this.hasApplySectionTarget) this.applySectionTarget.classList.toggle("hidden", !inChooser)
    if (this.hasExampleBtnTarget)   this.exampleBtnTarget.classList.toggle("hidden", !this._exampleSlug)
    this.element.classList.remove("hidden"); this.element.classList.add("flex")
  }
  close() { this.element.classList.add("hidden"); this.element.classList.remove("flex"); this._clearError() }
  backdrop(e) { if (e.target === this.element) this.close() }
  stop(e) { e.stopPropagation() }

  toggleTarget() {
    const on = this.targetFieldsTarget.classList.toggle("hidden") === false
    this.targetToggleTarget.setAttribute("aria-checked", on ? "true" : "false")
    this.targetToggleTarget.classList.toggle("bg-primary-fixed", on)
    this.targetToggleTarget.classList.toggle("bg-surface-container-highest", !on)
    this.targetKnobTarget.classList.toggle("translate-x-5", on)
  }

  // ---- actions ----
  startBlank() { this._post(this.createUrlValue, JSON.stringify(this._payload()), { json: true }) }

  startAi() {
    if (!this.paidValue) return this._upgrade()
    this._busy(this.aiBtnTarget, this.aiLabelTarget, "Drafting…")
    this._post(this.aiUrlValue, JSON.stringify(this._payload()), { json: true })
      .finally(() => this._unbusy(this.aiBtnTarget, this.aiLabelTarget, "Start with AI"))
  }

  pickPdf() { if (!this.paidValue) return this._upgrade(); this.fileTarget.click() }

  uploadPdf() {
    const file = this.fileTarget.files && this.fileTarget.files[0]
    if (!file) return
    const fd = new FormData()
    fd.append("file", file)
    const p = this._payload()
    fd.append("resume_name", p.resume_name); fd.append("template_id", p.template_id || "")
    fd.append("job_target[title]", p.job_target.title || "")
    fd.append("job_target[company]", p.job_target.company || ""); fd.append("job_target[description]", p.job_target.description || "")
    this._busy(this.pdfBtnTarget, this.pdfLabelTarget, "Parsing…")
    this._post(this.pdfUrlValue, fd, { json: false })
      .finally(() => { this._unbusy(this.pdfBtnTarget, this.pdfLabelTarget, "Import PDF"); this.fileTarget.value = "" })
  }

  startLinkedin() {
    const el = document.querySelector('[data-controller~="linkedin-import"]')
    if (!el) return this._error("LinkedIn import is unavailable.")
    const ctrl = this.application.getControllerForElementAndIdentifier(el, "linkedin-import")
    if (!ctrl) return this._error("LinkedIn import is unavailable.")
    ctrl.submitUrlValue = this.linkedinUrlValue   // redirect to the resume import endpoint
    // Pass the modal fields through the LinkedIn flow so the eventual
    // POST to App::ResumesController#import_linkedin carries the user's
    // resume name + target-job context. Without this, LinkedIn-imported
    // resumes are saved as "Untitled resume" and lose the target-job
    // payload that blank/AI/PDF paths preserve.
    ctrl.resumePayloadValue = this._payload()
    this.close()
    ctrl.openLinkedInImport()
  }

  // Apply the chosen template to a COPY of one of the user's own résumés.
  applyToResume(e) {
    const id = e.currentTarget && e.currentTarget.dataset ? e.currentTarget.dataset.resumeId : ""
    if (!id) return
    this._post(this.createUrlValue, JSON.stringify({ ...this._payload(), source_resume_id: id }), { json: true })
  }

  // Start from the example's model content (examples gallery only).
  startFromExample() {
    if (!this._exampleUseUrl) return
    this._post(this._exampleUseUrl, JSON.stringify(this._payload()), { json: true })
  }

  // ---- helpers ----
  _payload() {
    return {
      resume_name: this._val(this.nameTarget),
      experience_level: this._val(this.levelTarget),
      template_id: this._templateId || "",
      job_target: {
        title: this._val(this.jtTitleTarget), company: this._val(this.jtCompanyTarget), description: this._val(this.jtDescTarget)
      }
    }
  }
  _val(t) { return t ? (t.value || "").trim() : "" }

  async _post(url, body, { json }) {
    this._clearError()
    try {
      const headers = { "Accept": "application/json", "X-CSRF-Token": this._csrf() }
      if (json) headers["Content-Type"] = "application/json"
      const res = await fetch(url, { method: "POST", credentials: "same-origin", headers, body })
      if (res.status === 403) return this._upgrade()
      const data = await res.json().catch(() => ({}))
      if (data && data.ok && data.editUrl) { window.location = data.editUrl; return }
      this._error((data && data.error) || "Something went wrong. Please try again.")
    } catch (e) { this._error("Network error. Please try again.") }
  }

  _busy(btn, label, text) { btn.disabled = true; if (label) { btn.dataset.prev = label.textContent; label.textContent = text } }
  _unbusy(btn, label, text) { btn.disabled = false; if (label) label.textContent = text }
  _csrf() { return document.querySelector('meta[name="csrf-token"]')?.content || "" }
  _error(msg) { if (this.hasErrorTarget) { this.errorTarget.textContent = msg; this.errorTarget.classList.remove("hidden") } }
  _clearError() { if (this.hasErrorTarget) this.errorTarget.classList.add("hidden") }
  _upgrade() {
    // Prefer the in-app upgrade modal (mounted in the app layout for non-paid
    // users). Close this modal first so the upgrade modal isn't stacked behind
    // it. Fall back to a direct Stripe redirect only if it isn't on the page.
    if (document.getElementById("app-upgrade-modal")) {
      this.close()
      window.dispatchEvent(new CustomEvent("app-upgrade-modal:open"))
    } else {
      window.location = "/users/premium_subscribe_direct"
    }
  }
}
